import React, { useState } from "react";
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { settings } from "app";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { Button, RecordContextProvider, ReferenceField, TextField, useNotify, useRecordContext } from "react-admin";
import dataProvider from "server/dataProvider";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { getFileFromUrl, previewFileFromUrl } from "server/components/DownloadFile";
import { documentRequests } from "api/documentsApi";
import { studentRequests } from "api/studentApi";
import { DocumentAction } from "utils/constants";
import DeclarationMacrosModal from "./DeclarationMacrosModal";

// Compact icon action with a tooltip; swaps the icon for a spinner while its request is pending.
const RowAction = ({ title, icon, loading, disabled, onClick }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        color="primary"
        disabled={disabled}
        onClick={onClick}
        sx={{
          border: 1,
          borderColor: theme => alpha(theme.palette.primary.main, 0.3),
          "&:hover": { bgcolor: theme => alpha(theme.palette.primary.main, 0.08) },
        }}
      >
        {loading ? <CircularProgress size={18} /> : icon}
      </IconButton>
    </span>
  </Tooltip>
);

const ROW_ACTIONS = [
  { action: DocumentAction.Preview, title: "Преглед", icon: <VisibilityOutlinedIcon fontSize="small" /> },
  { action: DocumentAction.Download, title: "Изтегли", icon: <FileDownloadOutlinedIcon fontSize="small" /> },
];

const DeclarationRow = ({ declaration, needsMacros, actions }) => (
  <RecordContextProvider value={declaration}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.25,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        transition: "border-color 150ms, background-color 150ms",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 1.5,
          color: "primary.main",
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <DescriptionOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ReferenceField
          source="id"
          reference={documentRequests.getDynamicDeclarationsSelect()}
          link={false}
        >
          <TextField
            source="name"
            sx={{ fontWeight: 500, wordBreak: "break-word" }}
          />
        </ReferenceField>
        {needsMacros && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
          >
            <EditNoteIcon sx={{ fontSize: 16 }} />
            Изисква попълване на допълнителни данни
          </Typography>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexShrink: 0 }}
      >
        {actions}
      </Stack>
    </Box>
  </RecordContextProvider>
);

const EmptyState = () => (
  <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
    <FolderOffOutlinedIcon sx={{ fontSize: 48, opacity: 0.5 }} />
    <Typography sx={{ mt: 1 }}>Няма налични декларации за този студент</Typography>
  </Box>
);

const documentsCountLabel = count => {
  if (!count) return "Няма налични документи";
  return count === 1 ? "1 документ за преглед и изтегляне" : `${count} документа за преглед и изтегляне`;
};

const DeclarationsDialog = ({ label = "Декларации" }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const [ids, setIds] = useState([]);
  // { [declarationId]: [{ key, name, isRequired, options: [{ value, label }] }] } - macros the BE cannot prefill;
  // the user types them in, or picks one of `options` when the macro has any.
  const [customMacros, setCustomMacros] = useState({});
  // { id, action } of the request in flight - drives the row spinner and blocks double clicks.
  const [pending, setPending] = useState(null);

  const downloadUrl = declarationId =>
    `${settings.gatewayApi}/${studentRequests.downloadStudentEducationDeclaration(
      record.studentEducationId,
      declarationId,
    )}`;

  const getCustomMacros = dynamicDeclaration => customMacros[dynamicDeclaration.id] || [];

  const handleAction = async (dynamicDeclaration, action) => {
    const request = action === DocumentAction.Preview ? previewFileFromUrl : getFileFromUrl;
    setPending({ id: dynamicDeclaration.id, action });
    try {
      // DownloadFile helpers notify on failure themselves.
      await request(downloadUrl(dynamicDeclaration.id), null, notify, "POST", { macros: {} });
    } finally {
      setPending(null);
    }
  };

  // Declarations with custom macros open the macros form (the button becomes its trigger);
  // the rest are fetched straight away.
  const renderActions = dynamicDeclaration => {
    const macros = getCustomMacros(dynamicDeclaration);
    const rowPending = pending?.id === dynamicDeclaration.id ? pending.action : null;

    return ROW_ACTIONS.map(({ action, title, icon }) => {
      const button = (
        <RowAction
          title={title}
          icon={icon}
          loading={rowPending === action}
          disabled={Boolean(rowPending)}
          onClick={() => handleAction(dynamicDeclaration, action)}
        />
      );

      return macros.length ? (
        <DeclarationMacrosModal
          key={action}
          action={action}
          macros={macros}
          url={downloadUrl(dynamicDeclaration.id)}
          triggerButton={button}
        />
      ) : (
        <React.Fragment key={action}>{button}</React.Fragment>
      );
    });
  };

  const fetchDeclarations = async () => {
    if (!record?.studentEducationId) return;

    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationDeclarations(record.studentEducationId),
        null,
        "GET",
      );
      setIds((data.dynamicDeclarationIds || []).map(id => ({ id })));
      setCustomMacros(data.customMacros || {});
    } catch (error) {
      // 404 means the student has no declarations - open the modal with an empty list.
      if (error?.status === 404) {
        setIds([]);
        setCustomMacros({});
        return;
      }
      notify("Грешка при зареждане на декларациите", { type: "error" });
      return false;
    }
  };

  return (
    <ConfirmModal
      triggerButton={
        <Button
          label={label}
          startIcon={<DocumentScannerIcon />}
        />
      }
      titleContent="Декларации"
      subtitle={documentsCountLabel(ids.length)}
      onOpen={fetchDeclarations}
      isConfirmButton={false}
      dialogSize="md"
    >
      {ids.length ? (
        <Stack
          spacing={1}
          sx={{ py: 2 }}
        >
          {ids.map(declaration => (
            <DeclarationRow
              key={declaration.id}
              declaration={declaration}
              needsMacros={getCustomMacros(declaration).length > 0}
              actions={renderActions(declaration)}
            />
          ))}
        </Stack>
      ) : (
        <EmptyState />
      )}
    </ConfirmModal>
  );
};

export default DeclarationsDialog;
